import {
  ActivityIdSchema,
  DeliverableIdSchema,
  ElementIdSchema,
  MethodIdSchema,
} from '@dpem/ontology';
import * as z from 'zod';

import {
  ActorRoleSchema,
  BindingKindSchema,
  BindingOverrideActionSchema,
  DecisionKindSchema,
  ParameterValueTypeSchema,
  WorkshopStateSchema,
} from '../common/enums.js';
import { PageQuerySchema, paginated } from '../common/pagination.js';
import {
  DescriptionSchema,
  EmailSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  MarkdownSchema,
  ParameterKeySchema,
  ShortTextSchema,
  TextFilterSchema,
} from '../common/primitives.js';
import { ELEMENT_FIELD_TABLES, clearableFields } from './method-graph.js';
import { MethodVersionSummarySchema } from './methods.js';

/**
 * workshop context — facilitated adoption sessions.
 *
 * A session records INCLUDE / ADAPT / SKIP per element, and — because skipping is not
 * tailoring — three further kinds of client configuration: a **field override** per
 * element, a **binding override** per activity, and the **parameters** the client
 * supplies. Then it asks publishing to emit the tailored variant. Workshop never writes
 * JSON-LD itself (architecture §2 context map).
 *
 * The four are one vocabulary on purpose. "Adopt DPSE at Acme" is not a list of phases to
 * drop: it is *this* phase renamed to their language, *that* activity's purpose rewritten,
 * Jira swapped for Azure DevOps on eleven activities, and a two-week sprint length that
 * half the writeups quote. A session that could only skip would leave the other three in
 * a slide deck, and the published variant would be a prescriptive method with holes in
 * it rather than the client's own method.
 */

export const ParticipantSchema = z.strictObject({
  id: IdentifierSchema,
  displayName: LabelSchema,
  email: EmailSchema.nullable(),
  organisationRole: ShortTextSchema.nullable(),
  /** Platform role when the participant is also a user of the tool. */
  actorRole: ActorRoleSchema.nullable(),
  /** Participants may attend without a platform account. */
  userId: IdentifierSchema.nullable(),
  addedAt: IsoDateTimeSchema,
});
export type Participant = z.infer<typeof ParticipantSchema>;

export const TailoringDecisionSchema = z.strictObject({
  elementId: ElementIdSchema,
  decision: DecisionKindSchema,
  /** MTH-013 warns when an ADAPT or SKIP carries no rationale. */
  rationale: DescriptionSchema.nullable(),
  decidedAt: IsoDateTimeSchema,
  decidedBy: ShortTextSchema,
});
export type TailoringDecision = z.infer<typeof TailoringDecisionSchema>;

export const TemplateOverrideSchema = z.strictObject({
  deliverableId: DeliverableIdSchema,
  /** Body as tailored in the session; published as a new TemplateVersion. */
  body: MarkdownSchema,
  baseVersionNumber: z.int().min(1).nullable(),
  updatedAt: IsoDateTimeSchema,
  updatedBy: ShortTextSchema,
});
export type TemplateOverride = z.infer<typeof TemplateOverrideSchema>;

/* ── client configuration ───────────────────────────────────────────────────
 *
 * Three shapes, one idea: everything a client changes about a method that is not
 * "leave it out". Each is held against the session until publish, when authoring
 * replays them onto the derived draft with the same domain commands an author uses —
 * so a tailored variant is validated by the ontology exactly as an authored one is,
 * and there is no second implementation of "rename an element".
 */

/**
 * A field patch against one element of the baseline, in the client's words.
 *
 * The field tables are authoring's, imported rather than restated
 * (`ELEMENT_FIELD_TABLES`): an override that could set a field the author route cannot
 * would be an override publish has no command to apply, and the reverse — a field
 * authors can edit and workshops cannot — is the gap this whole schema exists to close.
 *
 * `name` sits beside them rather than inside them because authoring keeps renaming on its
 * own route, for the reason stated there: a drag and a text edit must not arrive as one
 * ambiguous command. Absent means "keep the baseline name"; unlike the fields, it cannot
 * be cleared, because an element with no name is not an element.
 *
 * Absent field means "leave the baseline value alone", explicit `null` means "clear it
 * for this client" — the same distinction the PATCH route draws, and the reason a
 * regulated client can empty a gate's `automatedCheck` rather than merely ignore it.
 */
const overrideBranch = <const TKind extends string, TFields extends z.ZodRawShape>(
  kind: TKind,
  fields: TFields,
) =>
  z.strictObject({
    elementId: ElementIdSchema,
    kind: z.literal(kind),
    name: LabelSchema.optional(),
    ...clearableFields(fields),
  });

export const ElementOverrideSchema = z.discriminatedUnion('kind', [
  overrideBranch('phase', ELEMENT_FIELD_TABLES.phase),
  overrideBranch('stage', ELEMENT_FIELD_TABLES.stage),
  overrideBranch('workPractice', ELEMENT_FIELD_TABLES.workPractice),
  overrideBranch('activity', ELEMENT_FIELD_TABLES.activity),
  overrideBranch('role', ELEMENT_FIELD_TABLES.role),
  overrideBranch('gate', ELEMENT_FIELD_TABLES.gate),
  overrideBranch('lens', ELEMENT_FIELD_TABLES.lens),
  overrideBranch('ceremony', ELEMENT_FIELD_TABLES.ceremony),
]);
export type ElementOverride = z.infer<typeof ElementOverrideSchema>;

/**
 * One activity's use of one catalogue thing, as the client actually works.
 *
 * This is the answer to "we do not use Jira, we use Azure DevOps", which the decision
 * vocabulary cannot express: SKIP on `tool:jira` drops it from every activity in the
 * method, and there is no decision at all that *adds* a tool the baseline never named.
 * So an override is a triple — activity, kind, target — plus what to do with it.
 *
 * `configuration` is the client's own detail about that binding: the instance, the
 * project key, the account. It is deliberately **not** written into the catalogue and not
 * written into the published ontology, because a `dpem:Tool` is one shared record and
 * "Acme's Jira project is ACME" is not a fact about Jira. It is carried to the plan layer,
 * which is where tool setup and asset configuration belong — the work a client does
 * *before* a project launches.
 */
export const BindingOverrideSchema = z.strictObject({
  activityId: ActivityIdSchema,
  bindingKind: BindingKindSchema,
  targetId: ElementIdSchema,
  action: BindingOverrideActionSchema.exclude(['CLEAR']),
  configuration: DescriptionSchema.nullable(),
  updatedAt: IsoDateTimeSchema,
  updatedBy: ShortTextSchema,
});
export type BindingOverride = z.infer<typeof BindingOverrideSchema>;

/**
 * A value the client supplies once, that the tailored method then reads.
 *
 * This is what turns a prescriptive method into a parametrised one. DPSE says "run a
 * sprint"; Acme runs two-week sprints in Azure DevOps with a three-person review board,
 * and those three facts appear in a dozen writeups. Repeating them a dozen times is how a
 * tailored method goes stale the first time one of them changes, so they are declared
 * once here and referenced as `{{sprint.length}}` in any text the session overrides.
 * Substitution happens on publish, so the client's document reads plainly and the session
 * still records *why* it reads that way.
 *
 * `value` is nullable: a parameter can be declared in the room and answered afterwards,
 * which is the normal order of an adoption workshop. An unanswered parameter substitutes
 * to nothing and is reported at publish rather than silently leaving `{{...}}` in the
 * client's method.
 */
export const MethodParameterSchema = z.strictObject({
  id: IdentifierSchema,
  key: ParameterKeySchema,
  label: LabelSchema,
  description: DescriptionSchema.nullable(),
  valueType: ParameterValueTypeSchema,
  /** Non-empty only for `ENUM`; the choices the client picks between. */
  allowedValues: z.array(ShortTextSchema).max(32),
  value: ShortTextSchema.nullable(),
  updatedAt: IsoDateTimeSchema,
  updatedBy: ShortTextSchema,
});
export type MethodParameter = z.infer<typeof MethodParameterSchema>;

export const WorkshopSessionSchema = z.strictObject({
  id: IdentifierSchema,
  name: LabelSchema,
  clientName: LabelSchema.nullable(),
  state: WorkshopStateSchema,
  /** The method version being tailored. */
  sourceMethodVersionId: IdentifierSchema,
  sourceMethodName: LabelSchema,
  /** Set once the tailored variant is published. */
  publishedMethodVersionId: IdentifierSchema.nullable(),
  facilitatedBy: ShortTextSchema,
  participants: z.array(ParticipantSchema),
  decisions: z.array(TailoringDecisionSchema),
  templateOverrides: z.array(TemplateOverrideSchema),
  elementOverrides: z.array(ElementOverrideSchema),
  bindingOverrides: z.array(BindingOverrideSchema),
  parameters: z.array(MethodParameterSchema),
  notes: MarkdownSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  completedAt: IsoDateTimeSchema.nullable(),
});
export type WorkshopSession = z.infer<typeof WorkshopSessionSchema>;

export const WorkshopSessionSummarySchema = WorkshopSessionSchema.omit({
  participants: true,
  decisions: true,
  templateOverrides: true,
  elementOverrides: true,
  bindingOverrides: true,
  parameters: true,
  notes: true,
}).extend({
  participantCount: z.int().min(0),
  decisionCount: z.int().min(0),
  overrideCount: z.int().min(0),
  /**
   * How much of the session is configuration rather than exclusion. Three counts and not
   * one, because a list row that said "17 tailorings" would hide the difference between a
   * session that renamed seventeen phases and one that rebound seventeen activities to a
   * different tracker — and those are different engagements.
   */
  elementOverrideCount: z.int().min(0),
  bindingOverrideCount: z.int().min(0),
  parameterCount: z.int().min(0),
});
export type WorkshopSessionSummary = z.infer<typeof WorkshopSessionSummarySchema>;

export const CreateWorkshopSessionRequestSchema = z.strictObject({
  name: LabelSchema,
  sourceMethodVersionId: IdentifierSchema,
  clientName: LabelSchema.optional(),
  notes: MarkdownSchema.optional(),
});
export type CreateWorkshopSessionRequest = z.infer<typeof CreateWorkshopSessionRequestSchema>;
export const CreateWorkshopSessionResponseSchema = z.strictObject({
  session: WorkshopSessionSchema,
});
export type CreateWorkshopSessionResponse = z.infer<typeof CreateWorkshopSessionResponseSchema>;

export const GetWorkshopSessionRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
});
export type GetWorkshopSessionRequest = z.infer<typeof GetWorkshopSessionRequestSchema>;
export const GetWorkshopSessionResponseSchema = z.strictObject({
  session: WorkshopSessionSchema,
  sourceMethod: MethodVersionSummarySchema,
});
export type GetWorkshopSessionResponse = z.infer<typeof GetWorkshopSessionResponseSchema>;

export const ListWorkshopSessionsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  /**
   * `q` alone, rather than the whole of `TextFilterSchema`: that schema also carries
   * `tags`, and a session has none — it is an engagement with a client, not a catalogue
   * entry. Declaring a filter the server cannot honour is worse than omitting it, because
   * a caller who sends it gets an unfiltered page and no indication why.
   */
  q: TextFilterSchema.shape.q,
  state: WorkshopStateSchema.optional(),
  sourceMethodVersionId: IdentifierSchema.optional(),
});
export type ListWorkshopSessionsRequest = z.infer<typeof ListWorkshopSessionsRequestSchema>;
export const ListWorkshopSessionsResponseSchema = paginated(WorkshopSessionSummarySchema);
export type ListWorkshopSessionsResponse = z.infer<typeof ListWorkshopSessionsResponseSchema>;

export const AddParticipantRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  displayName: LabelSchema,
  email: EmailSchema.optional(),
  organisationRole: ShortTextSchema.optional(),
  userId: IdentifierSchema.optional(),
});
export type AddParticipantRequest = z.infer<typeof AddParticipantRequestSchema>;
export const AddParticipantResponseSchema = z.strictObject({
  participant: ParticipantSchema,
  participantCount: z.int().min(1),
});
export type AddParticipantResponse = z.infer<typeof AddParticipantResponseSchema>;

export const RecordTailoringDecisionRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  elementId: ElementIdSchema,
  decision: DecisionKindSchema,
  rationale: DescriptionSchema.optional(),
  /** Cascade the same decision to contained elements (stage → its activities). */
  applyToDescendants: z.boolean().optional(),
});
export type RecordTailoringDecisionRequest = z.infer<typeof RecordTailoringDecisionRequestSchema>;
export const RecordTailoringDecisionResponseSchema = z.strictObject({
  decision: TailoringDecisionSchema,
  /** Decisions written by the cascade, if any. */
  cascaded: z.array(TailoringDecisionSchema),
  decisionCount: z.int().min(0),
});
export type RecordTailoringDecisionResponse = z.infer<typeof RecordTailoringDecisionResponseSchema>;

export const OverrideTemplateBodyRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  deliverableId: DeliverableIdSchema,
  body: MarkdownSchema,
  baseVersionNumber: z.int().min(1).optional(),
});
export type OverrideTemplateBodyRequest = z.infer<typeof OverrideTemplateBodyRequestSchema>;
export const OverrideTemplateBodyResponseSchema = z.strictObject({
  override: TemplateOverrideSchema,
});
export type OverrideTemplateBodyResponse = z.infer<typeof OverrideTemplateBodyResponseSchema>;

/**
 * Set (or replace) the field override on one element.
 *
 * Whole-override replacement rather than a patch-of-a-patch. The client sends the override
 * it wants the element to end up with, and what it omits is *not overridden* — which is
 * the only reading under which "undo the rename but keep the rewritten purpose" is
 * expressible. A merging PATCH would make an omitted key mean "keep my previous override",
 * and there would then be no way to take one back short of a second verb.
 *
 * An override with no name and no field at all is refused: `workshopClearElementOverride`
 * says that, and saying it two ways is how the two come to disagree.
 */
const setOverrideBranch = <const TKind extends string, TFields extends z.ZodRawShape>(
  kind: TKind,
  fields: TFields,
) =>
  z.strictObject({
    sessionId: IdentifierSchema,
    ...overrideBranch(kind, fields).shape,
  });

export const SetElementOverrideRequestSchema = z.discriminatedUnion('kind', [
  setOverrideBranch('phase', ELEMENT_FIELD_TABLES.phase),
  setOverrideBranch('stage', ELEMENT_FIELD_TABLES.stage),
  setOverrideBranch('workPractice', ELEMENT_FIELD_TABLES.workPractice),
  setOverrideBranch('activity', ELEMENT_FIELD_TABLES.activity),
  setOverrideBranch('role', ELEMENT_FIELD_TABLES.role),
  setOverrideBranch('gate', ELEMENT_FIELD_TABLES.gate),
  setOverrideBranch('lens', ELEMENT_FIELD_TABLES.lens),
  setOverrideBranch('ceremony', ELEMENT_FIELD_TABLES.ceremony),
]);
export type SetElementOverrideRequest = z.infer<typeof SetElementOverrideRequestSchema>;
export const SetElementOverrideResponseSchema = z.strictObject({
  override: ElementOverrideSchema,
  /**
   * The decision the override rides on, marked ADAPT. Returned for the same reason
   * `workshopOverrideTemplate` returns one: overriding a field *is* adapting the element,
   * so the badge beside it has just changed and the caller should not have to re-read the
   * session to find out.
   */
  decision: TailoringDecisionSchema,
  elementOverrideCount: z.int().min(0),
});
export type SetElementOverrideResponse = z.infer<typeof SetElementOverrideResponseSchema>;

export const ClearElementOverrideRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  elementId: ElementIdSchema,
});
export type ClearElementOverrideRequest = z.infer<typeof ClearElementOverrideRequestSchema>;
export const ClearElementOverrideResponseSchema = z.strictObject({
  elementId: ElementIdSchema,
  /**
   * The decision as it stands afterwards, or null when clearing the override removed the
   * only reason there was a decision row at all. Both are real outcomes: an element the
   * room adapted *and* argued about keeps its rationale, and one that was only ever
   * touched by a rename goes back to being undecided.
   */
  decision: TailoringDecisionSchema.nullable(),
  elementOverrideCount: z.int().min(0),
});
export type ClearElementOverrideResponse = z.infer<typeof ClearElementOverrideResponseSchema>;

/**
 * Bind, unbind or forget — one activity, one catalogue target.
 *
 * `CLEAR` is on this route rather than a DELETE of its own because the thing being
 * cleared is identified by a triple, and a triple in a URL is three path segments that
 * only ever appear together. The action is the verb; the triple is the subject.
 */
export const SetBindingOverrideRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  activityId: ActivityIdSchema,
  bindingKind: BindingKindSchema,
  targetId: ElementIdSchema,
  action: BindingOverrideActionSchema,
  /** The client's instance, project or account for this binding. Plan data, not ontology. */
  configuration: DescriptionSchema.optional(),
});
export type SetBindingOverrideRequest = z.infer<typeof SetBindingOverrideRequestSchema>;
export const SetBindingOverrideResponseSchema = z.strictObject({
  /** Null when the action was `CLEAR`. */
  override: BindingOverrideSchema.nullable(),
  decision: TailoringDecisionSchema.nullable(),
  bindingOverrideCount: z.int().min(0),
});
export type SetBindingOverrideResponse = z.infer<typeof SetBindingOverrideResponseSchema>;

/**
 * Declare a parameter, or answer one.
 *
 * One route for both because they are one row and the room does them in either order —
 * a facilitator who types the client's sprint length has declared and answered in the
 * same gesture, and one who lists eight open questions on a whiteboard has declared
 * eight and answered none.
 */
export const SetMethodParameterRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  key: ParameterKeySchema,
  label: LabelSchema,
  description: DescriptionSchema.optional(),
  valueType: ParameterValueTypeSchema,
  allowedValues: z.array(ShortTextSchema).max(32).optional(),
  /** Explicit `null` un-answers a parameter; absent leaves the current answer alone. */
  value: ShortTextSchema.nullable().optional(),
});
export type SetMethodParameterRequest = z.infer<typeof SetMethodParameterRequestSchema>;
export const SetMethodParameterResponseSchema = z.strictObject({
  parameter: MethodParameterSchema,
  parameterCount: z.int().min(0),
});
export type SetMethodParameterResponse = z.infer<typeof SetMethodParameterResponseSchema>;

export const DeleteMethodParameterRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  key: ParameterKeySchema,
});
export type DeleteMethodParameterRequest = z.infer<typeof DeleteMethodParameterRequestSchema>;
export const DeleteMethodParameterResponseSchema = z.strictObject({
  key: ParameterKeySchema,
  parameterCount: z.int().min(0),
  /**
   * Overridden text still holding `{{key}}` after the parameter went. Reported rather
   * than refused: the facilitator may be about to rewrite those very fields, and a
   * platform that blocked the delete would make them hunt the references by hand.
   */
  danglingReferenceElementIds: z.array(ElementIdSchema),
});
export type DeleteMethodParameterResponse = z.infer<typeof DeleteMethodParameterResponseSchema>;

export const CompleteWorkshopSessionRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  notes: MarkdownSchema.optional(),
});
export type CompleteWorkshopSessionRequest = z.infer<typeof CompleteWorkshopSessionRequestSchema>;
export const CompleteWorkshopSessionResponseSchema = z.strictObject({
  session: WorkshopSessionSchema,
  /** Elements with no recorded decision, which default to INCLUDE on publish. */
  undecidedElementIds: z.array(ElementIdSchema),
});
export type CompleteWorkshopSessionResponse = z.infer<
  typeof CompleteWorkshopSessionResponseSchema
>;

/**
 * One entry from the adoption's own catalogue, as a publish ledger line.
 *
 * `standsInFor` is `ElementIdSchema` and not a `@id` reference in the graph sense: it names
 * the platform entry the client is *not* using, which is by construction absent from the
 * published document.
 */
export const ClientCatalogueEntrySchema = z.strictObject({
  kind: BindingKindSchema,
  id: ElementIdSchema,
  name: LabelSchema,
  broughtBy: LabelSchema.nullable(),
  standsInFor: ElementIdSchema.nullable(),
});
export type ClientCatalogueEntry = z.infer<typeof ClientCatalogueEntrySchema>;

export const PublishTailoredVariantRequestSchema = z.strictObject({
  sessionId: IdentifierSchema,
  /** Canonical id of the tailored method — a variant is an edge, not a type. */
  methodId: MethodIdSchema,
  name: LabelSchema,
  description: DescriptionSchema.optional(),
  /** Publish despite warning-severity diagnostics. Errors still block. */
  acknowledgeWarnings: z.boolean().optional(),
});
export type PublishTailoredVariantRequest = z.infer<typeof PublishTailoredVariantRequestSchema>;
export const PublishTailoredVariantResponseSchema = z.strictObject({
  session: WorkshopSessionSummarySchema,
  methodVersionId: IdentifierSchema,
  publishedAt: IsoDateTimeSchema,
  checksum: z.string().regex(/^[0-9a-f]{64}$/),
  skippedElementCount: z.int().min(0),
  adaptedElementCount: z.int().min(0),
  /**
   * What the client configuration actually did to the graph, as opposed to what the
   * session asked for. The two differ whenever an override went stale — a session outlives
   * the version it was run against — and a facilitator reading the record in the room
   * needs the number that describes their client's method.
   */
  appliedElementOverrideCount: z.int().min(0),
  appliedBindingOverrideCount: z.int().min(0),
  substitutedParameterCount: z.int().min(0),
  /**
   * Overrides the source graph could not take: the element is gone, or was skipped by
   * this very session, or the catalogue target no longer exists. Reported, never silently
   * dropped — the same contract `omittedUnknown` has for stale skips.
   */
  unappliedOverrides: z.array(
    z.strictObject({
      elementId: ElementIdSchema,
      reason: z.enum(['NOT_IN_GRAPH', 'SKIPPED', 'TARGET_NOT_FOUND', 'NOT_AN_ACTIVITY']),
      detail: ShortTextSchema,
    }),
  ),
  /** Parameters referenced by overridden text that the client never answered. */
  unansweredParameterKeys: z.array(ParameterKeySchema),
  /**
   * What became of the catalogue this client brought.
   *
   * `carried` entries are materialised into the published document — node, provenance and
   * all — and are inside `checksum`, so they survive the session being deleted or the
   * entry being promoted later. `staged` entries were brought and bound to nothing, so
   * they are not in the variant: publishing an unreferenced node would put an orphan in
   * the document, and dropping it without a word would lose an afternoon of the client's
   * cataloguing in silence.
   */
  clientCatalogue: z.strictObject({
    carried: z.array(ClientCatalogueEntrySchema),
    staged: z.array(ClientCatalogueEntrySchema),
  }),
});
export type PublishTailoredVariantResponse = z.infer<typeof PublishTailoredVariantResponseSchema>;
